export interface VideoAspectRatio {
  /** Tailwind utility that pins the media box to the ratio. */
  className: string;
  /** Width divided by height, used to translate a max-height into the equivalent max-width. */
  ratio: number;
}

const ASPECT_RATIO_16_9: VideoAspectRatio = { className: 'aspect-video', ratio: 16 / 9 };
const ASPECT_RATIO_9_16: VideoAspectRatio = { className: 'aspect-[9/16]', ratio: 9 / 16 };
const ASPECT_RATIO_1_1: VideoAspectRatio = { className: 'aspect-square', ratio: 1 };
const ASPECT_RATIO_4_3: VideoAspectRatio = { className: 'aspect-[4/3]', ratio: 4 / 3 };
const ASPECT_RATIO_3_4: VideoAspectRatio = { className: 'aspect-[3/4]', ratio: 3 / 4 };

function findVideoAspectRatio(value: string | undefined): VideoAspectRatio | undefined {
  switch (value) {
    case '16:9':
      return ASPECT_RATIO_16_9;
    case '9:16':
      return ASPECT_RATIO_9_16;
    case '1:1':
      return ASPECT_RATIO_1_1;
    case '4:3':
      return ASPECT_RATIO_4_3;
    case '3:4':
      return ASPECT_RATIO_3_4;
    default:
      return undefined;
  }
}

/**
 * Maps a Crystallize `aspect-ratio` selection onto its Tailwind class and numeric ratio.
 * Each tile passes the ratio its own shape defaults to, so an unset or unknown selection still
 * renders the proportions the editors expect.
 */
export function getVideoAspectRatio(
  value: string | undefined,
  fallback: '16:9' | '9:16',
): VideoAspectRatio {
  const fallbackRatio = fallback === '16:9' ? ASPECT_RATIO_16_9 : ASPECT_RATIO_9_16;

  return findVideoAspectRatio(value) ?? fallbackRatio;
}

/**
 * A video box is constrained by width only — a max-height is translated into the equivalent
 * width so the aspect ratio survives and the `fill` poster is never stretched.
 */
export function getVideoMaxWidth(
  maxWidth: number | undefined,
  maxHeight: number | undefined,
  ratio: number,
): number | undefined {
  const widthFromHeight = maxHeight ? maxHeight * ratio : undefined;
  const candidates = [maxWidth, widthFromHeight].filter(
    (candidate): candidate is number => typeof candidate === 'number' && candidate > 0,
  );

  if (candidates.length === 0) {
    return undefined;
  }

  return Math.min(...candidates);
}
